import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, Typography, Select, InputNumber, Upload,
  Space, message, Image, Spin
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import productService from '../services/productService';
import api from '../services/api';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ProductEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { tenant } = useAuth();
  const tenantId = tenant?.id;
  const isEdit = !!id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (tenantId) {
      fetchCategories();
      if (isEdit) fetchProduct();
    }
  }, [id, tenantId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productService.getProduct(id, tenantId);
      if (response.success) {
        const product = response.data;
        form.setFieldsValue({
          name: product.name,
          description: product.description,
          sellingPoints: product.sellingPoints,
          price: product.price,
          category: product.category,
          miniprogramLink: product.miniprogramLink,
          tags: product.tags,
          status: product.status,
          sortOrder: product.sortOrder,
        });
        setImages(product.images || []);
      }
    } catch (error) {
      message.error('获取商品信息失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await productService.getCategories(tenantId);
      if (response.success) setCategories(response.data || []);
    } catch (error) { /* ignore */ }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const data = {
        ...values,
        tenantId,
        images,
      };
      let response;
      if (isEdit) {
        response = await productService.updateProduct(id, data);
      } else {
        response = await productService.createProduct(data);
      }
      if (response.success) {
        message.success(isEdit ? '更新成功' : '创建成功');
        navigate('/ai-content?tab=products');
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const [uploading, setUploading] = useState(false);

  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/aigc/upload-image?tenantId=' + tenantId, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data?.success && response.data.data) {
      return {
        url: response.data.data.url || response.data.data.ossUrl,
        ossUrl: response.data.data.ossUrl || response.data.data.url,
        mediaId: response.data.data.mediaId || null,
        usedCount: 0,
        lastUsedAt: null,
      };
    }
    throw new Error(response.data?.message || '上传失败');
  };

  const handleUpload = async (file) => {
    try {
      setUploading(true);
      const newImage = await uploadSingleFile(file);
      setImages(prev => [...prev, newImage]);
      message.success('图片上传成功');
    } catch (error) {
      message.error(error.message || '图片上传失败');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleBatchUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    const newImages = [];
    for (const file of fileList) {
      try {
        const img = await uploadSingleFile(file);
        newImages.push(img);
        successCount++;
      } catch (e) {
        failCount++;
      }
    }
    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
    }
    setUploading(false);
    if (successCount > 0 && failCount === 0) {
      message.success(`成功上传 ${successCount} 张图片`);
    } else if (successCount > 0) {
      message.warning(`成功 ${successCount} 张，失败 ${failCount} 张`);
    } else {
      message.error('图片上传失败');
    }
    return false;
  };

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/ai-content?tab=products')}>返回</Button>
        <Title level={5} style={{ margin: 0 }}>{isEdit ? '编辑商品' : '新增商品'}</Title>
      </div>
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ status: 'ACTIVE', sortOrder: 0 }} style={{ maxWidth: 700 }}>
          <Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}>
            <Input placeholder="输入商品名称" maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="商品描述">
            <TextArea rows={3} placeholder="描述商品特点、卖点等" maxLength={1000} showCount />
          </Form.Item>
          <Form.Item name="sellingPoints" label="卖点关键词" tooltip="AI生成文案时会优先使用这些关键词，即使没有详细描述也能生成好文案">
            <Input placeholder="多个卖点用逗号分隔，如：鲜榨,0添加,当日现做" maxLength={500} />
          </Form.Item>
          <Space size={16}>
            <Form.Item name="price" label="价格（元）">
              <InputNumber min={0} precision={2} placeholder="0.00" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="category" label="分类">
              <Select placeholder="选择或输入分类" allowClear showSearch style={{ width: 200 }}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                {categories.map(c => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }}>
                <Option value="ACTIVE">上架</Option>
                <Option value="INACTIVE">下架</Option>
              </Select>
            </Form.Item>
          </Space>
          <Form.Item name="miniprogramLink" label="小程序链接">
            <Input placeholder="小程序页面链接（可选）" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder="多个标签用逗号分隔，如：热销,新品,推荐" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序（数值越小越靠前）">
            <InputNumber min={0} style={{ width: 120 }} />
          </Form.Item>

          <Form.Item label="商品图片">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {images.map((img, index) => (
                <div key={index} style={{ position: 'relative', width: 104, height: 104 }}>
                  <Image src={img.ossUrl || img.url} width={104} height={104} style={{ objectFit: 'cover', borderRadius: 8 }} />
                  <Button type="text" danger icon={<DeleteOutlined />} size="small"
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(255,255,255,0.8)', borderRadius: '50%' }}
                    onClick={() => handleRemoveImage(index)} />
                  {img.usedCount > 0 && (
                    <div style={{ position: 'absolute', bottom: 2, left: 2, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 10, padding: '1px 4px', borderRadius: 4 }}>
                      已用{img.usedCount}次
                    </div>
                  )}
                </div>
              ))}
              <Upload
                showUploadList={false}
                beforeUpload={(file, fileList) => {
                  // Ant Design calls beforeUpload per file; only trigger on the first one
                  if (file === fileList[0]) {
                    if (fileList.length > 1) {
                      handleBatchUpload(fileList);
                    } else {
                      handleUpload(file);
                    }
                  }
                  return false;
                }}
                accept="image/*"
                multiple
                disabled={uploading}
              >
                <div style={{ width: 104, height: 104, border: '1px dashed #d9d9d9', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: uploading ? 'wait' : 'pointer', background: '#fafafa' }}>
                  {uploading ? <Spin size="small" /> : <PlusOutlined />}
                  <span style={{ fontSize: 12, marginTop: 4 }}>{uploading ? '上传中...' : '上传图片'}</span>
                </div>
              </Upload>
            </div>
            <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>支持批量选择多张图片上传，建议正方形图片，支持 JPG/PNG 格式</div>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>{isEdit ? '保存修改' : '创建商品'}</Button>
              <Button onClick={() => navigate('/ai-content?tab=products')}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ProductEdit;
